import React, { useState, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { EXAM_DATA, EXAM_CATEGORIES } from '../data/examData';
import { saveRecord } from '../utils/storage';
import { practiceTopics } from '../data/awsClfGuideData';
import { sapPracticeGroups } from '../data/awsSapGuideData';

const CLF_TOTAL = practiceTopics.reduce((n, g) => n + g.q.length, 0);
const SAP_TOTAL = sapPracticeGroups.reduce((n, g) => n + g.questions.length, 0);

const FullBankCard: React.FC<{ to: string; image: string; title: string; meta: string; desc: string; cta: string }> = ({ to, image, title, meta, desc, cta }) => (
  <Link to={to} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: '#fff', border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius)', textDecoration: 'none', transition: '.2s' }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--blue-light)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--gray-200)'; e.currentTarget.style.boxShadow = 'none'; }}>
    <img src={image} alt="" style={{ width: '104px', height: 'auto', flexShrink: 0 }} />
    <div>
      <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--gray-800)' }}>{title}</div>
      <div style={{ fontSize: '0.74rem', fontWeight: 700, color: 'var(--orange)', margin: '1px 0 4px' }}>{meta}</div>
      <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', lineHeight: 1.5 }}>{desc}</div>
      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--blue)', marginTop: '6px' }}>{cta} →</div>
    </div>
  </Link>
);

type Step = 'category' | 'exam' | 'result';

const PracticeExamPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Check URL params to restore state
  useEffect(() => {
    const category = searchParams.get('category');
    if (category && EXAM_DATA[category]) {
      setCurrentCat(category);
      setStep('exam');
    }
  }, [searchParams]);

  const [step, setStep] = useState<Step>('category');
  const [currentCat, setCurrentCat] = useState<string | null>(null);
  const [userAnswers, setUserAnswers] = useState<Record<number, number>>({});
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [scoreData, setScoreData] = useState<{ score: number; total: number } | null>(null);
  const { msg, type, visible, showToast } = useToast();

  const startExam = (catId: string) => {
    // Clear previous answers and set new category in URL
    setUserAnswers({});
    setChecked({});
    setSubmitted(false);
    setScoreData(null);
    setCurrentCat(catId);
    setSearchParams({ category: catId });
    setStep('exam');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectAnswer = (qIdx: number, optIdx: number) => {
    if (checked[qIdx]) return;
    setUserAnswers(prev => ({ ...prev, [qIdx]: optIdx }));
  };

  const checkAnswer = (qIdx: number) => {
    if (userAnswers[qIdx] === undefined) {
      showToast('⚠️ Please select an answer first.', 'error');
      return;
    }
    setChecked(prev => ({ ...prev, [qIdx]: true }));
  };

  const showAnswerDirect = (qIdx: number) => {
    setChecked(prev => ({ ...prev, [qIdx]: true }));
  };

  const submitExam = useCallback(() => {
    if (!currentCat) return;
    const data = EXAM_DATA[currentCat];
    const total = data.questions.length;
    let score = 0;

    // Mark all as checked
    const allChecked: Record<number, boolean> = {};
    data.questions.forEach((_, i) => { allChecked[i] = true; });
    setChecked(allChecked);
    setSubmitted(true);

    data.questions.forEach((q, i) => {
      if (userAnswers[i] === q.ans) score++;
    });

    saveRecord('exam_submissions', {
      category: currentCat,
      categoryName: data.name,
      score,
      total,
      percentage: Math.round(score / total * 100),
      answers: { ...userAnswers },
      source: 'practice_exam',
    });

    // Clear URL params after submission
    setSearchParams({});

    setScoreData({ score, total });
    setStep('result');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentCat, userAnswers, setSearchParams]);

  const backToCategories = () => {
    setCurrentCat(null);
    setUserAnswers({});
    setChecked({});
    setSubmitted(false);
    setScoreData(null);
    setSearchParams({});
    setStep('category');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const data = currentCat ? EXAM_DATA[currentCat] : null;
  const totalQ = data?.questions.length ?? 10;
  const answeredCount = Object.keys(userAnswers).length;
  const progressPct = Math.round((answeredCount / totalQ) * 100);

  return (
    <Layout>
      <Toast msg={msg} type={type} visible={visible} />

      <div className="page-hero">
        <span className="section-tag">Practice Exam</span>
        <h1>Test Your <span className="text-blue">Knowledge</span></h1>
        <p>Select a category below. Get 10 free sample questions — check each answer as you go, or reveal after submitting.</p>
      </div>

      <div className="section">
        <div className="container" style={{ maxWidth: '860px' }}>

          {/* STEP 1: Category Selection */}
          {step === 'category' && (
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 800, marginBottom: '6px', color: 'var(--gray-800)' }}>Choose a Category</h2>
              <p style={{ color: 'var(--gray-500)', fontSize: '0.9rem', marginBottom: '22px' }}>Each category has 10 free sample questions. Select to begin.</p>
              <div className="exam-cat-grid">
                {EXAM_CATEGORIES.map(cat => (
                  <button key={cat.id} className="exam-cat-card" onClick={() => startExam(cat.id)}>
                    <div className="cat-emoji">{cat.emoji}</div>
                    <div className="cat-name">{cat.name}</div>
                    <div className="cat-count">{cat.count}</div>
                  </button>
                ))}
              </div>
              <div style={{ marginTop: '30px' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '4px', color: 'var(--gray-800)' }}>Full Practice Question Banks</h2>
                <p style={{ color: 'var(--gray-500)', fontSize: '0.86rem', marginBottom: '14px' }}>Want more than the 10 samples? These topic-wise banks give instant feedback and a detailed explanation for every answer.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' }}>
                  <FullBankCard to="/aws-clf-c02-practice" image="/images/hub/practice-bg.svg" title="AWS Cloud Practitioner (CLF-C02)" meta={`${CLF_TOTAL} questions · ${practiceTopics.length} topics`} desc="Filter by topic, track your score and read why each option is right or wrong." cta="Start CLF-C02 practice" />
                  <FullBankCard to="/aws-sap-practice" image="/images/hub/architect-practice-bg.svg" title="AWS Solutions Architect Professional (SAP-C02)" meta={`${SAP_TOTAL} scenario questions · ${sapPracticeGroups.length} sets`} desc="By difficulty and mock exam, with single and multiple-answer questions." cta="Start SAP-C02 practice" />
                </div>
              </div>
              <div style={{ marginTop: '28px', padding: '18px', background: 'var(--blue-pale)', border: '1.5px solid var(--blue-soft)', borderRadius: 'var(--radius)' }}>
                <p style={{ fontSize: '0.88rem', color: 'var(--blue)', fontWeight: 600 }}>
                  🔓 <strong>Full Exam Bank:</strong> Unlock 200+ questions across all categories with an access key.{' '}
                  <Link to="/request" style={{ color: 'var(--orange)', textDecoration: 'underline', fontWeight: 700 }}>Request Access →</Link>
                </p>
              </div>
            </div>
          )}

          {/* STEP 2: Questions */}
          {step === 'exam' && data && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <button className="btn btn-outline-gray btn-sm" onClick={backToCategories}>← Back to Categories</button>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 600 }}>
                    {answeredCount} of {totalQ} answered
                  </div>
                  <div className="progress-bar-wrap" style={{ width: '180px', marginTop: '5px' }}>
                    <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                </div>
              </div>

              <div style={{ background: 'var(--blue-pale)', border: '1.5px solid var(--blue-soft)', borderRadius: 'var(--radius)', padding: '14px 18px', marginBottom: '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.2rem' }}>{data.emoji}</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--blue)' }}>{data.name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }}>10 sample questions · Check each answer individually</div>
                </div>
              </div>

              {data.questions.map((q, qIdx) => {
                const isChecked = checked[qIdx];
                const selected = userAnswers[qIdx];
                return (
                  <div className="q-card" key={qIdx}>
                    <div className="q-header">
                      <div className="q-num">{qIdx + 1}</div>
                      <div className="q-text" style={{ whiteSpace: 'pre-wrap' }}>{q.q}</div>
                    </div>
                    <div className="q-options">
                      {q.opts.map((opt, optIdx) => {
                        let cls = '';
                        if (isChecked) {
                          if (optIdx === q.ans) cls = 'correct';
                          else if (optIdx === selected && selected !== q.ans) cls = 'wrong';
                        } else if (optIdx === selected) {
                          cls = 'selected';
                        }
                        return (
                          <div
                            key={optIdx}
                            className={`q-opt${cls ? ' ' + cls : ''}`}
                            onClick={() => !isChecked && selectAnswer(qIdx, optIdx)}
                            style={isChecked ? { pointerEvents: 'none' } : {}}
                          >
                            <input type="radio" readOnly checked={optIdx === selected} />
                            <span style={{ whiteSpace: 'pre-wrap' }}>{String.fromCharCode(65 + optIdx)}. {opt}</span>
                          </div>
                        );
                      })}
                    </div>
                    {isChecked ? (
                      <div className={`q-answer-box show ${selected === q.ans ? 'correct-ans' : 'wrong-ans'}`}>
                        {selected === q.ans
                          ? <><strong>✅ Correct!</strong></>
                          : <><strong>❌ Incorrect.</strong> Correct answer: <strong>{String.fromCharCode(65 + q.ans)}. {q.opts[q.ans]}</strong></>
                        }
                        <br /><br /><strong>Explanation:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{q.explain}</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                        <button className="btn btn-blue btn-sm" onClick={() => checkAnswer(qIdx)}>Check Answer</button>
                        <button className="btn btn-outline-gray btn-sm" onClick={() => showAnswerDirect(qIdx)}>Show Answer</button>
                      </div>
                    )}
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '24px' }}>
                <button className="btn btn-blue btn-lg" onClick={submitExam} disabled={submitted}>Submit All & See Score</button>
                <button className="btn btn-outline-gray" onClick={backToCategories}>Try Another Category</button>
              </div>
            </div>
          )}

          {/* STEP 3: Results */}
          {step === 'result' && data && scoreData && (() => {
            const pct = Math.round((scoreData.score / scoreData.total) * 100);
            const wrong = scoreData.total - scoreData.score;
            let emoji = '📚', title = 'Keep Practicing!', resultMsg = 'Review the explanations below and keep going. Request full access for more practice questions.';
            if (pct >= 80) { emoji = '🏆'; title = 'Outstanding!'; resultMsg = "Excellent preparation! You're ready for the real exam. Unlock the full bank to stay sharp."; }
            else if (pct >= 60) { emoji = '👍'; title = 'Good Work!'; resultMsg = 'Solid attempt! A bit more practice will get you exam-ready. Unlock 200+ questions to push higher.'; }
            return (
              <div>
                <button className="btn btn-outline-gray btn-sm" onClick={backToCategories} style={{ marginBottom: '18px' }}>← Try Another Category</button>
                <div className="score-card">
                  <div style={{ fontSize: '3rem', marginBottom: '8px' }}>{emoji}</div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--gray-800)' }}>{title} — {data.name}</h2>
                  <div style={{ fontSize: '3.2rem', fontWeight: 800, color: 'var(--blue)', margin: '14px 0', fontFamily: "'Sora',sans-serif" }}>{scoreData.score}/{scoreData.total} ({pct}%)</div>
                  <p style={{ color: 'var(--gray-600)', maxWidth: '460px', margin: '0 auto 22px' }}>{resultMsg}</p>
                  <div style={{ maxWidth: '380px', margin: '0 auto 24px' }}>
                    {[
                      { label: '✅ Correct', count: scoreData.score, pct, cls: 'var(--green)' },
                      { label: '❌ Wrong', count: wrong, pct: (wrong / scoreData.total) * 100, cls: 'var(--red)' },
                    ].map((bar, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                        <span style={{ fontSize: '0.84rem', fontWeight: 700, minWidth: '80px', color: 'var(--gray-700)' }}>{bar.label}</span>
                        <div style={{ flex: 1, background: 'var(--gray-200)', borderRadius: '50px', height: '10px' }}>
                          <div style={{ height: '10px', borderRadius: '50px', background: bar.cls, width: `${bar.pct}%` }} />
                        </div>
                        <span style={{ fontSize: '0.82rem', color: 'var(--gray-500)', fontWeight: 700, minWidth: '28px' }}>{bar.count}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {currentCat === 'ccp' && <Link to="/aws-clf-c02-practice" className="btn btn-blue">Practise all {CLF_TOTAL} CLF-C02 questions →</Link>}
                    <Link to="/request" className="btn btn-orange">Unlock Full Access →</Link>
                    <button className="btn btn-outline-blue" onClick={backToCategories}>Try Another Category</button>
                  </div>
                </div>

                {/* Detailed Review */}
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: 800, marginBottom: '16px', color: 'var(--gray-800)' }}>📋 Detailed Answer Review</h3>
                  {data.questions.map((q, i) => {
                    const isCorrect = userAnswers[i] === q.ans;
                    return (
                      <div className="q-card" key={i}>
                        <div className="q-header">
                          <div className="q-num" style={{ background: isCorrect ? 'var(--green-pale)' : 'var(--red-pale)', color: isCorrect ? 'var(--green)' : 'var(--red)' }}>
                            {isCorrect ? '✓' : '✗'}
                          </div>
                          <div className="q-text" style={{ whiteSpace: 'pre-wrap' }}>{q.q}</div>
                        </div>
                        <div style={{ fontSize: '0.85rem', marginBottom: '6px', color: isCorrect ? '#166534' : 'var(--red)' }}>
                          {isCorrect ? '✅ You answered correctly!' : `❌ Your answer: ${userAnswers[i] !== undefined ? `${String.fromCharCode(65 + userAnswers[i])}. ${q.opts[userAnswers[i]]}` : 'Not answered'}`}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '8px', fontWeight: 600 }}>
                          ✅ Correct: {String.fromCharCode(65 + q.ans)}. {q.opts[q.ans]}
                        </div>
                        <div style={{ fontSize: '0.84rem', color: 'var(--gray-600)', background: 'var(--gray-100)', padding: '10px', borderRadius: '8px', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                          <strong>💡 Explanation:</strong> {q.explain}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Access CTA Banner */}
      <div style={{ background: 'linear-gradient(135deg,var(--blue),#1e3a8a)', padding: '52px 6%', textAlign: 'center', color: '#fff' }}>
        <h2 style={{ fontSize: '1.6rem', fontWeight: 800, marginBottom: '10px' }}>Want 200+ Questions?</h2>
        <p style={{ opacity: 0.85, maxWidth: '460px', margin: '0 auto 24px', lineHeight: 1.7 }}>Request full exam access and get your unique access key to unlock the complete question bank.</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/request" className="btn btn-lg" style={{ background: '#fff', color: 'var(--blue)' }}>Request Full Access</Link>
          <Link to="/login" className="btn btn-lg" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)' }}>I Have an Access Key</Link>
        </div>
      </div>
    </Layout>
  );
};

export default PracticeExamPage;
